import { useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $userAccountsStore, $userCardsStore } from '@lib/store';
import { cn } from '@shared/utils';
import type { UserResponse } from '@shared/api';
import { UserCards } from '../userCards';
import { RemoveCardsModal } from '../modals';
import { AccountTileSmall, AddCardsModal, View } from '@widgets';
import { Button, Text } from '@shared/widgets';

interface AccountsCardsProps {
  className?: string;
  user: UserResponse;
}

export const AccountsAndCards = (props: AccountsCardsProps) => {
  const userAccountsStore = useUnit($userAccountsStore.store);
  const userCardsStore = useUnit($userCardsStore.store);

  const [showAddCards, setShowAddCards] = useState<boolean>(false);
  const [showRemoveCards, setShowRemoveCards] = useState<boolean>(false);

  const { t } = useTranslation('user');

  return (
    <section className={cn('flex flex-col gap-6', props.className)}>
      <div className='flex flex-wrap items-center justify-between gap-4'>
        <Text weight='medium' size='2xl'>
          Payments Accounts ({userAccountsStore.data.length})
        </Text>
        <div className='flex items-center gap-3'>
          <Button color='outlined' onClick={() => setShowAddCards(true)}>
            {t('buttons.add', { ns: 'common' })}
          </Button>
          {(userAccountsStore.data.length > 0 || userCardsStore.data.data.length > 0) && (
            <Button color='outlined' onClick={() => setShowRemoveCards(true)}>
              {t('buttons.remove', { ns: 'common' })}
            </Button>
          )}
        </div>
      </div>
      <View
        loading={userAccountsStore.loading}
        error={userAccountsStore.error}
        onRefresh={() => $userAccountsStore.events.getUserAccounts({ AssignedUserId: props.user.id })}
        noData={!userAccountsStore.data.length}
        noDataText={t('noData.noRecords', { ns: 'common' })}
      >
        <div className={cn('flex flex-wrap gap-[30px]', props.user.locked && 'pointer-events-none select-none opacity-40')}>
          {userAccountsStore.data.map((item, idx) => (
            <AccountTileSmall key={idx} className={cn('w-[calc(50%-15px)]', 'max-[1400px]:w-full')} account={item} />
          ))}
        </div>
      </View>
      <Text weight='medium' size='2xl'>
        Cards ({userCardsStore.data.data.length})
      </Text>
      <UserCards userId={props.user.id} />
      <AddCardsModal
        isOpen={showAddCards}
        onClose={() => setShowAddCards(false)}
        user={props.user}
        accountsItems={userAccountsStore.data}
        cardsItems={userCardsStore.data.data}
        onSubmit={async (data) =>
          await Promise.all([
            data.accountIds.length > 0 && $userAccountsStore.events.addAccounts(data),
            data.cardIds.length > 0 && $userCardsStore.events.addCards(data),
          ])
        }
      />
      <RemoveCardsModal isOpen={showRemoveCards} user={props.user} onClose={() => setShowRemoveCards(false)} />
    </section>
  );
};
